import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSdk } from '../../sdkContext';
import config from '../../config';
import { fetchSubscription, updateSubscriptionPayment } from './api';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import css from './SubscriptionCheckoutPage.css';

const PaymentPage = ({ params, intl }) => {
  const sdk = useSdk();
  const history = useHistory();
  const cardElementRef = useRef(null);
  const stripeInstance = useRef(null);
  const cardInstance = useRef(null);

  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { subId } = params;

  const { data: subscription, isLoading } = useQuery(
    ['subscription', subId],
    () => fetchSubscription(sdk, subId),
    {
      enabled: !!subId,
      onSuccess: (data) => {
        const { processState } = data.attributes;

        if (processState !== 'payment-pending') {
          history.replace(`/suborder/${subId}/details`);
        }
      }
    }
  );

  const syncMutation = useMutation(
    () => updateSubscriptionPayment(sdk, subId),
    {
      onSuccess: () => {
        history.push(`/suborder/${subId}/details`);
      },
      onError: (err) => {
        setError(
          intl.formatMessage(
            { id: 'SubscriptionPaymentPage.syncError' },
            { errorMessage: err.message }
          )
        );
        setIsProcessing(false);
      }
    }
  );

  useEffect(() => {
    if (window.Stripe && subscription && cardElementRef.current) {
      if (!stripeInstance.current) {
        stripeInstance.current = window.Stripe(config.stripe.publishableKey);
      }

      if (!cardInstance.current) {
        const elements = stripeInstance.current.elements();

        cardInstance.current = elements.create('card', {
          hidePostalCode: true,
          style: {
            base: {
              color: '#1a1f36',
              fontFamily: 'inherit',
              fontSize: '16px',
              '::placeholder': {
                color: '#a3acb9',
              },
            },
            invalid: {
              color: '#f44336',
              iconColor: '#f44336',
            },
          }
        });

        cardInstance.current.mount(cardElementRef.current);
      }
    }

    return () => {
      if (cardInstance.current) {
        try {
          cardInstance.current.destroy();
        } catch (e) {
        }
        cardInstance.current = null;
      }
    };
  }, [subscription]);

  const handleConfirmSetup = async () => {
    if (isProcessing || syncMutation.isLoading) return;

    setIsProcessing(true);
    setError(null);

    const clientSecret = subscription?.attributes?.stripeSetupIntent?.clientSecret;

    if (!clientSecret) {
      setError(intl.formatMessage({ id: 'SubscriptionPaymentPage.clientSecretError' }));
      setIsProcessing(false);
      return;
    }

    if (!cardInstance.current) {
      setError(intl.formatMessage({ id: 'SubscriptionPaymentPage.cardElementError' }, { defaultValue: 'Błąd elementu płatności. Odśwież stronę.' }));
      setIsProcessing(false);
      return;
    }

    try {
      const result = await stripeInstance.current.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardInstance.current,
          billing_details: {
            name: `${subscription.attributes.firstName} ${subscription.attributes.lastName}`,
            email: subscription.attributes.email,
          },
        },
      });

      if (result.error) {
        setError(result.error.message);
        setIsProcessing(false);
      } else if (result.setupIntent.status === 'succeeded') {
        syncMutation.mutate();
      }
    } catch (err) {
      setError(err.message);
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className={css.loader}>
        <FormattedMessage id="SubscriptionPaymentPage.initializing" />
      </div>
    );
  }

  const { title } = subscription?.listing?.attributes || {};
  const { amount, currency } = subscription?.listing.attributes?.price || {};
  const minDurationMonths = subscription?.listing.attributes?.minDurationMonths || 1;

  const initialAmount = amount * minDurationMonths;
  const monthlyAmount = amount;

  const currentLocale = intl.locale === 'pl' ? 'pl-PL' : (intl.locale === 'cs' ? 'cs-CZ' : 'hu-HU');
  const formatPrice = (val) => new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currency || 'PLN'
  }).format(val / 100);

  const firstImage = subscription?.listing?.images?.[0];
  const imageUrl = firstImage?.attributes?.variants?.['square-small']?.url ||
    firstImage?.attributes?.variants?.['landscape-crop']?.url;

  // Pobranie danych dostawy z subskrypcji
  const deliveryType = subscription?.attributes?.deliveryType || subscription?.attributes?.meta?.delivery?.deliveryType;
  const deliveryToHome = subscription?.attributes?.deliveryToHome;
  const metaDelivery = subscription?.attributes?.meta?.delivery;
  const providerAttributes = subscription?.deliveryToProvider?.attributes;

  const isHomeDelivery = deliveryType === 'home';
  const isStorePickup = deliveryType === 'store' || deliveryType === 'pickup';

  // Wyznaczenie nazwy lub adresu sklepu
  const storeLocation =
    providerAttributes?.name ||
    providerAttributes?.businessLocation?.search ||
    providerAttributes?.businessLocation?.selectedPlace?.address;

  // Wyznaczenie pełnego adresu dla dostawy do domu
  const homeStreet = deliveryToHome?.address || metaDelivery?.streetAddress;
  const homeCity = deliveryToHome?.city || metaDelivery?.postalCodeAndCity;
  const homeAddressFormatted = [homeStreet, homeCity].filter(Boolean).join(', ');

  return (
    <Page title={intl.formatMessage({ id: 'CheckoutPage.title' })}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer hideCategories />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain>
          <div className={css.paymentContainer}>
            <div className={css.paymentContent}>
              <h1 className={css.pageTitle}>
                <FormattedMessage id="SubscriptionPaymentPage.heading" />
              </h1>

              <div className={css.orderSummary}>
                <div className={css.productHeader}>
                  {imageUrl ? (
                    <img src={imageUrl} alt={title} className={css.productImage} />
                  ) : (
                    <div className={css.imagePlaceholder} />
                  )}
                  <div className={css.productInfo}>
                    <span className={css.label}>
                      <FormattedMessage id="SubscriptionPaymentPage.productLabel" />
                    </span>
                    <h3 className={css.productTitle}>{title}</h3>
                  </div>
                </div>

                {(isHomeDelivery || isStorePickup) && (
                  <div className={css.deliveryDetails}>
                    <span className={css.subLabel}>
                      {isStorePickup && (
                        <FormattedMessage id="SubscriptionPaymentPage.deliveryTypePickupLabel" defaultValue="Odbiór w sklepie" />
                      )}
                      {isHomeDelivery && (
                        <FormattedMessage id="SubscriptionPaymentPage.deliveryTypeHomeLabel" defaultValue="Dostawa kurierem" />
                      )}
                    </span>
                    <span className={css.deliveryValue}>
                      {isStorePickup && (storeLocation || '-')}
                      {isHomeDelivery && (homeAddressFormatted || '-')}
                    </span>
                  </div>
                )}

                <div className={css.divider} />

                <div className={css.summaryRow}>
                  <div className={css.priceLabelGroup}>
                    <span className={css.mainPriceLabel}>
                      <FormattedMessage
                        id="SubscriptionPaymentPage.initialPaymentLabel"
                        values={{ count: minDurationMonths }}
                      />
                    </span>
                  </div>
                  <span className={css.price}>
                    {formatPrice(initialAmount)}
                  </span>
                </div>

                <div className={css.recurringRow}>
                  <span className={css.recurringLabel}>
                    <FormattedMessage
                      id="SubscriptionPaymentPage.recurringPaymentLabel"
                      values={{ nextMonth: minDurationMonths + 1 }}
                    />
                  </span>
                  <span className={css.recurringPrice}>
                    <FormattedMessage
                      id="SubscriptionPaymentPage.monthlyPriceFormat"
                      values={{ price: formatPrice(monthlyAmount) }}
                    />
                  </span>
                </div>

                <div className={css.infoNote}>
                  <FormattedMessage id="SubscriptionPaymentPage.rentalStartNote" />
                </div>
              </div>

              <div className={css.stripeSection}>
                <label className={css.inputLabel}>
                  <FormattedMessage id="SubscriptionPaymentPage.cardDetails" />
                </label>
                <div className={css.stripeInputContainer}>
                  <div ref={cardElementRef} className={css.cardElement} />
                </div>
              </div>

              {error && (
                <div className={css.errorAlert}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <button
                onClick={handleConfirmSetup}
                disabled={isProcessing || syncMutation.isLoading}
                className={css.submitBtn}
              >
                {isProcessing || syncMutation.isLoading ? (
                  <div className={css.loaderWrapper}>
                    <span className={css.loaderText}>
                      <FormattedMessage id="SubscriptionPaymentPage.processing" />
                    </span>
                  </div>
                ) : (
                  <FormattedMessage id="SubscriptionPaymentPage.submitBtn" />
                )}
              </button>

              <p className={css.disclaimer}>
                <FormattedMessage id="SubscriptionPaymentPage.disclaimer" />
              </p>

              <div className={css.securityBadge}>
                <FormattedMessage id="SubscriptionPaymentPage.securityBadge" />
              </div>
            </div>
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

export default injectIntl(PaymentPage);